/**
 * accessScopeTree — Pure utilities for constructing and updating the Access panel scope tree.
 * Pure, no Firebase imports.
 */

import type { CanvasElement, CanvasElementType, AccessScope, NodeGrant } from '../types/schema'
import type { WorkspaceRole } from '../types/workspace'
import { resolveNodeAccess, type NodeAccessResult } from './accessScope'

export interface AccessScopeTreeNode {
  id: string
  title: string
  type: CanvasElementType
  depth: number
  parentId: string | null
  ownGrant: NodeGrant | null
  effective: NodeAccessResult
  inheritedFrom: { id: string; title: string } | null
  canView: boolean
  canEdit: boolean
}

export interface BuildAccessScopeTreeInput {
  elements: Array<
    Pick<CanvasElement, 'id' | 'title' | 'type'> &
    Partial<Pick<CanvasElement, 'parentId' | 'board' | 'deletedAt' | 'deletedFromBoard'>> &
    Record<string, unknown>
  >
  role: WorkspaceRole
  accessScope?: AccessScope
}

export interface SetNodeGrantResult extends AccessScope {
  overLimit?: boolean
}

const TYPE_PRIORITY: Record<CanvasElementType, number> = {
  GOAL: 0,
  TASK: 1,
  SUBTASK: 2,
}

const GRANT_STRENGTH: Record<string, number> = {
  NONE: 0,
  VIEW: 1,
  EDIT: 2,
}

function isDeletedOrBinned(el: BuildAccessScopeTreeInput['elements'][number]): boolean {
  if (el.board === 'BIN') return true
  if (el.deletedAt) return true
  if (el.deletedFromBoard) return true
  if ((el as Record<string, unknown>).isDeleted) return true
  return false
}

function compareElements(
  a: BuildAccessScopeTreeInput['elements'][number],
  b: BuildAccessScopeTreeInput['elements'][number],
): number {
  const pa = TYPE_PRIORITY[a.type] ?? 99
  const pb = TYPE_PRIORITY[b.type] ?? 99
  return pa - pb
}

/**
 * buildAccessScopeTree — orders nodes GOAL -> TASK -> SUBTASK, calculating
 * depth, ownGrant, effective access, inheritedFrom, and canView/canEdit.
 * Skipping deleted/binned elements; placing orphans at the end as top level.
 */
export function buildAccessScopeTree({
  elements,
  role,
  accessScope,
}: BuildAccessScopeTreeInput): AccessScopeTreeNode[] {
  const activeElements = elements.filter((el) => !isDeletedOrBinned(el))
  const elementMap = new Map<string, (typeof activeElements)[0]>()

  for (const el of activeElements) {
    elementMap.set(el.id, el)
  }

  const childrenMap = new Map<string, Array<(typeof activeElements)[0]>>()
  const normalRoots: Array<(typeof activeElements)[0]> = []
  const orphans: Array<(typeof activeElements)[0]> = []

  for (const el of activeElements) {
    const parentId = el.parentId
    if (!parentId || parentId.trim() === '') {
      normalRoots.push(el)
    } else if (elementMap.has(parentId)) {
      const list = childrenMap.get(parentId) ?? []
      list.push(el)
      childrenMap.set(parentId, list)
    } else {
      orphans.push(el)
    }
  }

  normalRoots.sort(compareElements)
  for (const list of childrenMap.values()) {
    list.sort(compareElements)
  }
  orphans.sort(compareElements)

  const result: AccessScopeTreeNode[] = []
  const visited = new Set<string>()

  const traverse = (
    el: (typeof activeElements)[0],
    depth: number,
    ancestors: Array<(typeof activeElements)[0]>,
  ): void => {
    if (visited.has(el.id)) return
    visited.add(el.id)

    const ancestryIds = [el.id, ...ancestors.map((a) => a.id)]
    const effective = resolveNodeAccess({ role, accessScope, ancestry: ancestryIds })

    const rawGrant =
      accessScope?.mode === 'RESTRICTED' ? accessScope.grants?.[el.id] : undefined
    const ownGrant: NodeGrant | null =
      rawGrant === 'VIEW' || rawGrant === 'EDIT' ? rawGrant : null

    const ownStrength = ownGrant ? GRANT_STRENGTH[ownGrant] : 0
    const effectiveStrength = GRANT_STRENGTH[effective]

    let inheritedFrom: { id: string; title: string } | null = null

    if (
      accessScope?.mode === 'RESTRICTED' &&
      role !== 'OWNER' &&
      role !== 'ADMIN' &&
      role !== 'HELPER' &&
      effectiveStrength > ownStrength
    ) {
      for (const anc of ancestors) {
        const ancGrant = accessScope.grants?.[anc.id]
        const ancStrength = ancGrant ? GRANT_STRENGTH[ancGrant] : 0
        if (ancStrength >= effectiveStrength) {
          inheritedFrom = { id: anc.id, title: anc.title }
          break
        }
      }
    }

    const canView = effective === 'VIEW' || effective === 'EDIT'
    const isCeilingViewOrNone =
      role === 'VIEWER' || role === 'READONLY' || role === 'HELPER'
    const canEdit = !isCeilingViewOrNone && effective === 'EDIT'

    result.push({
      id: el.id,
      title: el.title,
      type: el.type,
      depth,
      parentId: el.parentId ?? null,
      ownGrant,
      effective,
      inheritedFrom,
      canView,
      canEdit,
    })

    const children = childrenMap.get(el.id) ?? []
    for (const child of children) {
      traverse(child, depth + 1, [el, ...ancestors])
    }
  }

  for (const root of normalRoots) {
    traverse(root, 0, [])
  }

  for (const orphan of orphans) {
    traverse(orphan, 0, [])
  }

  // Any remaining unvisited active elements (e.g. cycles)
  for (const el of activeElements) {
    if (!visited.has(el.id)) {
      traverse(el, 0, [])
    }
  }

  return result
}

/**
 * setNodeGrant — returns a new AccessScope (never mutates).
 * NONE removes the grant key.
 * If adding a new grant would exceed 500 grants, returns overLimit: true
 * without adding the grant.
 */
export function setNodeGrant(
  scope: AccessScope,
  nodeId: string,
  value: 'NONE' | 'VIEW' | 'EDIT',
): SetNodeGrantResult {
  const currentGrants = scope?.grants ?? {}
  const nextGrants: Record<string, NodeGrant> = { ...currentGrants }

  if (value === 'NONE') {
    delete nextGrants[nodeId]
    return {
      mode: scope?.mode ?? 'RESTRICTED',
      grants: nextGrants,
      overLimit: false,
    }
  }

  const alreadyExists = Object.prototype.hasOwnProperty.call(currentGrants, nodeId)
  if (!alreadyExists && Object.keys(currentGrants).length >= 500) {
    return {
      mode: scope?.mode ?? 'RESTRICTED',
      grants: { ...currentGrants },
      overLimit: true,
    }
  }

  nextGrants[nodeId] = value
  return {
    mode: scope?.mode ?? 'RESTRICTED',
    grants: nextGrants,
    overLimit: false,
  }
}
